import { useNavigate } from 'react-router-dom';
import { useParlay } from '@/hooks/useParlay';

type SidebarItem = {
  id: string;
  label: string;
  icon: string;
  /** Supplied artwork; sports tiles use it instead of the font icon. */
  image?: string;
  href?: string;
};

const sportsSidebarItems: SidebarItem[] = [
  {
    id: 'inplay',
    label: 'In-Play',
    icon: 'icon-inplay_B',
    image: '/images/sports-ui/inplay.webp',
  },
  {
    id: 'today',
    label: 'Today',
    icon: 'icon-Today',
    image: '/images/sports-ui/today.webp',
  },
  {
    id: 'tomorrow',
    label: 'Tomorrow',
    icon: 'icon-Tomorrow',
    image: '/images/sports-ui/tomorrow.webp',
  },
  {
    id: 'leagues',
    label: 'Leagues',
    icon: 'icon-trophy',
    image: '/images/sports-ui/leagues.webp',
    href: '/leagues',
  },
  {
    id: 'parlay',
    label: 'MultiBet',
    icon: 'icon-parlay',
    image: '/images/sports-ui/multibet.webp',
    href: '/sports/all/inPlay',
  },
];

const casinoSidebarItems: SidebarItem[] = [
  {
    id: 'CATALOG',
    label: 'Catalog',
    icon: 'icon-catalog',
  },
  {
    id: 'LATEST',
    label: 'Latest',
    icon: 'icon-latest',
  },
  {
    id: 'A_Z',
    label: 'A-Z',
    icon: 'icon-A-Z',
  },
];

interface SportsSidebarProps {
  activeTab: string;
  variant?: 'sports' | 'casino';
  onTabChange: (tab: string) => void;
}

export default function SportsSidebar({
  activeTab,
  variant = 'sports',
  onTabChange,
}: SportsSidebarProps) {
  const navigate = useNavigate();
  const { setIsParlayEnabled } = useParlay();
  const sidebarItems = variant === 'casino' ? casinoSidebarItems : sportsSidebarItems;
  const isSports = variant === 'sports';

  return (
    <aside className={isSports ? 'h-full' : 'h-max rounded-xl bg-white p-1'}>
      <div className={isSports ? 'flex h-full flex-col gap-2' : 'flex flex-col gap-2'}>
        {sidebarItems.map((item) => {
          const active = activeTab === item.id;

          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === 'parlay') {
                  setIsParlayEnabled(true);
                }
                if (item.href) {
                  navigate(item.href);
                } else onTabChange(item.id);
              }}
              className={
                isSports
                  ? // flex-1 (not a fixed height) so all 5 tiles evenly share
                    // whatever height the sports cards column ends up being —
                    // otherwise this column (shorter, fixed-height tiles)
                    // doesn't stretch to match a taller cards column, and the
                    // last tile visibly falls short of it.
                    `flex flex-1 flex-col items-center justify-center gap-1.5 rounded-2xl ${
                      active
                        ? 'bg-gradient-to-b from-[#ffe94a] to-[#ffc400] shadow-[0_4px_10px_rgba(190,130,0,0.35),inset_0_1px_0_rgba(255,255,255,0.7)]'
                        : 'bg-gradient-to-b from-white to-[#fff6cf] shadow-[0_3px_8px_rgba(160,120,0,0.2),inset_0_1px_0_rgba(255,255,255,0.9)]'
                    }`
                  : `flex h-[5.2rem] flex-col items-center justify-center rounded-lg ${
                      active ? 'bg-[#ffcb05] text-black' : 'text-[#5f6875]'
                    }`
              }
            >
              {isSports && item.image ? (
                <img src={item.image} alt="" draggable={false} className="h-[2.6rem] w-auto" />
              ) : (
                <i className={`${item.icon} relative z-10 mb-1 text-[2rem]`} />
              )}

              <span
                className={
                  isSports
                    ? 'text-[0.75rem] font-semibold text-[#1c1c1c]'
                    : 'mt-2 text-[0.688rem] font-medium'
                }
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
